import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthService } from '../login/auth.service';
import { NotificationService } from '../services/notification.service';
import {
  ClientCar,
  Client,
  ApiResponse,
  UpdateClientDto,
  ClientPhone,
  ClientCallHistoryResponse,
  ClientProfileReportResponse
} from 'src/typings';
import { map, Observable } from 'rxjs';
import { isApiResponse } from 'src/typingCheck';

@Injectable({
  providedIn: 'root'
})
export class ClientService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private notificationService: NotificationService
  ) {}

  getList() {
    return this.http.get<Client[]>(`${environment.BACKEND}/client/list`);
  }

  getOne(id: number) {
    return this.http.get<Client>(`${environment.BACKEND}/client/${id}`);
  }

  getProfileReport(clientId: number, months = 3) {
    return this.http.get<ClientProfileReportResponse>(
      `${environment.BACKEND}/client/${clientId}/profile-report`, { params: { months } }
    );
  }

  getCallHistory(clientId: number, params?: { days?: number; limit?: number }) {
    return this.http.get<ClientCallHistoryResponse>(
      `${environment.BACKEND}/client/${clientId}/calls`,
      { params: params as any }
    );
  }

  createClientCar(payload: {
    vin?: string;
    description?: string;
    client_id?: number;
  }): Observable<ClientCar> {
    return this.http
      .post<{ success: boolean; error: boolean; message: ClientCar[] }>(
        `${environment.BACKEND}/client/create_car`,
        payload
      )
      .pipe(
        map((result) => {
          return result.message[0];
        })
      );
  }

  deleteClientCar(id: number) {
    return this.http.delete(`${environment.BACKEND}/client/car/${id}`);
  }

  updateClientCar(payload: { vin?: string; description?: string; client_id?: number; id: number }) {
    return this.http.put<Array<Array<ClientCar>>>(
      `${environment.BACKEND}/client/car/update`,
      payload
    );
  }

  save(payload: { id: number; name?: string; phone?: string | null }) {
    return this.http.put<{ success: boolean; error: boolean; message: Client }>(
      `${environment.BACKEND}/client/update`,
      payload
    );
  }

  search(payload?: {
    readonly phone?: string | null;
    readonly name?: string;
    readonly search_keys?: string;
    readonly id?: number;
    readonly limit?: number;
    readonly offset?: number;
    readonly search_string?: string | null;
  }) {
    return this.http.post<{ data: Client[]; length: number }>(
      `${environment.BACKEND}/client/search`,
      payload
    );
  }

  delete(id: number) {
    return this.http.delete(`${environment.BACKEND}/client/${id}`);
  }

  listPhones(clientId: number) {
    return this.http.get<ClientPhone[]>(`${environment.BACKEND}/client/${clientId}/phones`);
  }

  addPhone(clientId: number, payload: { phone: string }) {
    return this.http.post<ClientPhone[]>(
      `${environment.BACKEND}/client/${clientId}/phone`,
      payload
    );
  }

  updatePhone(clientId: number, phoneId: number, payload: { phone: string }) {
    return this.http.put<ClientPhone[]>(
      `${environment.BACKEND}/client/${clientId}/phone/${phoneId}`,
      payload
    );
  }

  deletePhone(clientId: number, phoneId: number) {
    return this.http.delete<ClientPhone[]>(
      `${environment.BACKEND}/client/${clientId}/phone/${phoneId}`
    );
  }

  setPrimaryPhone(clientId: number, phoneId: number) {
    return this.http.put<ClientPhone[]>(
      `${environment.BACKEND}/client/${clientId}/phone/${phoneId}/primary`,
      null
    );
  }

  create(
    name: string,
    phone: string,
    rating?: number,
    solvency?: number,
    smsNotificationsEnabled = true,
    paymentDelayDays?: number | null,
    debtLimit?: number | null
  ): Observable<Client | undefined> {
    return this.http
      .post<ApiResponse>(`${environment.BACKEND}/client/create`, {
        name,
        phone,
        rating,
        solvency,
        smsNotificationsEnabled,
        paymentDelayDays,
        debtLimit
      })
      .pipe(
        map((data: ApiResponse) => {
          if (isApiResponse(data) && data.error === true) {
            if (data.message) this.notificationService.showError(data.message);
            return undefined;
          } else {
            if (data.message) {
              const client: Client = JSON.parse(data.message);
              this.notificationService.notice(`Клиент ${client.name} создан!`);
              return client;
            }
            return;
          }
        })
      );
  }

  update(payload: UpdateClientDto) {
    return this.http.put<{ success: boolean; error: boolean; message: Client }>(
      `${environment.BACKEND}/client/update`,
      payload
    );
  }
}
